import axios, { isCancel } from './axios.js'
import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'
import sentryConfig from 'src/store/sentry.js'
import {
  getAuthHeader, getErrorForBreadcrumb,
  getRequestUser, handleResponse, notify, shouldLogOutUser,
  waitForPendingRefresh
} from 'src/store/vue-axios/interceptorHelper.js'
import {
  handle2faRateLimit,
  handleServerNotAvailable,
  handleOthers,
  isStepUpRequired,
  handleStepUpRequired
}
  from 'src/store/vue-axios/interceptorErrorHandler.js'

let refreshingTokenPromise = false

axios.interceptors.request.use(async function (config) {
  // TODO JHE 18.12.25: Disabled to investigate incident "Recovery Event: 2025-12-15 CDN incident + Network outage in Frankfurt"
  // const isBeta = await schoolIsBeta(config)
  // if (isBeta) {
  //   return {
  //     ...config,
  //     headers: {
  //       ...config.headers,
  //       'Sdui-Canary-Level': 'Beta'
  //     }
  //   }
  // }
  return config
})

// TODO remove global loading state:
axios.interceptors.request.use(async function (config) {
  await waitForPendingRefresh(config, refreshingTokenPromise)

  const authHeader = getAuthHeader(config)
  authHeader && (config.headers.Authorization = authHeader.Authorization)

  config.requestUser = await getRequestUser(config)

  return config
}, err => {
  return Promise.reject(err)
})

axios.interceptors.response.use(function (response) {
  // check if response is json otherwise step out
  if (!response?.headers['content-type']?.includes('application/json') && response?.config?.method?.toLowerCase() !== 'head') {
    console.error('axios response is not JSON, nor a HEAD request', { response })
    return
  }

  // get metadata from response
  // then start notification handler
  // todo: refactor:
  handleResponse(response.data.meta)
  notify(response.config)

  return { ...response.data, headers: response.headers }
}, async function (error) {
  // request was cancelled (cancelToken)
  if (isCancel(error)) return Promise.reject(error)

  // server not available / offline
  if (!error.response) {
    return handleServerNotAvailable(error)
  }

  if (error.response?.status === 503 && error.config.baseURL === import.meta.env.VITE_2FAL) {
    return handle2faRateLimit(error)
  }

  // other error
  if (error.response?.status !== 401) {
    return handleOthers(error)
  }

  // acr too low for a sensitive endpoint: needs a step-up login, not a token refresh
  if (isStepUpRequired(error)) {
    return handleStepUpRequired(error)
  }

  const originalRequest = error.config

  // prevent endless loop with retry
  if (originalRequest._retry) {
    return Promise.reject(error)
  }
  originalRequest._retry = true

  const userForRequest = originalRequest.requestUser
  const storedUsers = GlobalPrototypes.store.getters['multiUsers/storedUsers']
  const hasMultiAccounts = storedUsers && Object.keys(storedUsers).length > 1

  // auto logout user if refreshing of auth token isn't possible
  if (shouldLogOutUser(originalRequest)) {
    // user has multiple accounts, logout + remove the specific user + switch to next available user in store
    // user only has one account, logout from app + remove
    if (hasMultiAccounts) {
      await GlobalPrototypes.store.dispatch('multiUsers/removeUser', userForRequest)
    } else {
      await GlobalPrototypes.store.dispatch('users/autoLogout', userForRequest, { root: true })
    }

    return Promise.reject(error)
  }

  // only make one refresh request at a time and let other requests wait for this
  refreshingTokenPromise = refreshingTokenPromise || GlobalPrototypes.store.dispatch('users/refreshToken', { user: userForRequest }, { root: true })

  let refreshedAuthToken
  try {
    // refresh access_token
    refreshedAuthToken = await refreshingTokenPromise
  } catch (e) {
    sentryConfig.addBreadcrumb({
      category: 'auth/refreshToken error.message',
      message: getErrorForBreadcrumb(e.message),
      level: 'warning'
    })
    sentryConfig.addBreadcrumb({
      category: 'auth/refreshToken error.config.requestUser',
      message: e.config?.requestUser ? getErrorForBreadcrumb(e.config.requestUser) : 'null',
      level: 'warning'
    })
    sentryConfig.addBreadcrumb({
      category: 'has multiple accounts',
      message: `${hasMultiAccounts}`,
      level: 'warning'
    })
    sentryConfig.throwError('axios onReject intercept refresh failed')
    refreshingTokenPromise = null

    // TODO refactor when responses include unique error codes
    // then use the unique error code instead of the error message string
    if (typeof e.response?.data?.data === 'string' && e.response?.data?.data?.toLowerCase().includes('invalid user id')) {
      GlobalPrototypes.store.commit('users/SET_IS_INVALID_USER', true)
    }
    return
  }
  refreshingTokenPromise = null

  // set new access_token for old request
  originalRequest.headers.Authorization = `Bearer ${refreshedAuthToken.authToken}`
  // if the original request has actingAs (request for inactive user) we also need to overwrite its token because its
  // getting extracted in getAuthHeader()
  if (originalRequest.actingAs) {
    originalRequest.actingAs = { ...originalRequest.actingAs, authToken: refreshedAuthToken.authToken }
  }

  // retry request
  return axios(originalRequest)
})

export default axios
